import { RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { TableCell, TableRow } from '~/components/ui/table';

import { previewErrorKey } from '../utils/preview-error';

interface PreviewStateRowProps {
  /** Columns the single full-width cell should span. */
  colSpan: number;
  /** i18n key prefix for the tab, e.g. `review.preview` or `overview.preview`. */
  keyPrefix: string;
  isLoading: boolean;
  isError: boolean;
  /** True when there are no rows to show (`results.length === 0`). */
  isEmpty: boolean;
  /** Query error — selects a specific message in the error state. */
  error?: unknown;
  /** Re-runs the query; wired to the error "Try again" and empty "Refresh" controls. */
  onRetry: () => void;
}

/**
 * Shared loading / error / empty row for the Preview Products tables. Keeps the
 * inline-state markup (cause-specific error message, retry/refresh control) in
 * one place across both preview tabs. Returns null once there are rows to show —
 * which also length-guards the error row, so a failed background refetch that
 * keeps the last good rows never paints an error over them.
 */
export function PreviewStateRow({
  colSpan,
  keyPrefix,
  isLoading,
  isError,
  isEmpty,
  error,
  onRetry,
}: PreviewStateRowProps) {
  const { t } = useTranslation('content-enrichment');

  if (isLoading) {
    return (
      <TableRow data-testid="enrichment-preview-loading">
        <TableCell colSpan={colSpan} className="py-8 text-center text-sm text-gray-400">
          {t(`${keyPrefix}.loading`)}
        </TableCell>
      </TableRow>
    );
  }

  // Rows are present — nothing to overlay.
  if (!isEmpty) return null;

  if (isError) {
    return (
      <TableRow data-testid="enrichment-preview-error">
        <TableCell colSpan={colSpan} className="py-8 text-center">
          <div className="flex flex-col items-center gap-3">
            {/* Specific cause (auth / permission / not-ready / server / network) keyed off the ApiError code. */}
            <p className="text-destructive text-sm">
              {t(`${keyPrefix}.errors.${previewErrorKey(error)}`)}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="cursor-pointer gap-1.5"
              data-testid="enrichment-preview-retry-button"
            >
              <RotateCcw aria-hidden="true" className="h-4 w-4" />
              {t(`${keyPrefix}.retry`)}
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow data-testid="enrichment-preview-empty">
      <TableCell colSpan={colSpan} className="py-8 text-center">
        <div className="flex flex-col items-center gap-3">
          <div className="space-y-1">
            <p className="text-sm text-gray-500">{t(`${keyPrefix}.empty`)}</p>
            {/* Empty can mean "scores still processing" — guide the user to refresh. */}
            <p className="text-xs text-gray-400">{t(`${keyPrefix}.emptyHint`)}</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="cursor-pointer gap-1.5"
            data-testid="enrichment-preview-refresh-button"
          >
            <RotateCcw aria-hidden="true" className="h-4 w-4" />
            {t(`${keyPrefix}.refresh`)}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
